import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldAlt,
    faSearch,
    faBalanceScale,
    faCogs,
    faTasks,
    faChartLine,
    faFileInvoiceDollar
} from '@fortawesome/free-solid-svg-icons';
import AOS from 'aos';
import 'aos/dist/aos.css';

const services = [
    { icon: faFileInvoiceDollar, title: 'Financial Audit', description: 'Independent examination of financial statements to provide an opinion on their fairness and accuracy.' },
    { icon: faBalanceScale, title: 'Internal Audit', description: 'Review of internal controls and processes to improve operational efficiency and risk management.', color: 'text-success' },
    { icon: faSearch, title: 'Tax Audit', description: 'Assistance during tax audits initiated by the tax authorities to ensure compliance.', color: 'text-warning' },
    { icon: faCogs, title: 'Due Diligence Audit', description: 'Thorough investigation of a business’s financial and operational health for mergers and acquisitions.', color: 'text-info' },
    { icon: faShieldAlt, title: 'Risk Assessment', description: 'Identifying and evaluating potential risks that could impact business objectives.', color: 'text-danger' },
    { icon: faChartLine, title: 'Process & System Audit', description: 'Evaluating business processes and IT systems to identify inefficiencies and vulnerabilities.' },
];

const reasons = [
    { title: 'Independent & Objective', description: 'We provide unbiased insights into your financial health and internal controls.' },
    { title: 'Compliance & Assurance', description: 'Our audits ensure compliance with legal and regulatory requirements, providing peace of mind.' },
    { title: 'Operational Improvement', description: 'We don’t just find issues; we recommend practical solutions to enhance business performance.' },
    { title: 'Expert Team', description: 'Our auditors are highly qualified and experienced in a wide range of industries.' },
    { title: 'Confidentiality', description: 'We handle all your sensitive information with the utmost discretion and professionalism.' },
    { title: 'Strategic Partnership', description: 'We act as a trusted partner, helping you make informed decisions for sustainable growth.' },
];

const Auditax = () => {
    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
    }, []);

    const sectionStyles = {
        padding: '80px 0',
        fontFamily: 'Segoe UI, sans-serif'
    };

    const leadStyle = {
        color: '#6c757d',
        fontSize: '1.25rem'
    };

    const cardBaseStyle = {
        borderRadius: '15px',
        transition: 'all 0.3s ease',
        height: '100%',
        cursor: 'pointer'
    };

    const handleMouseEnter = (e) => {
        e.currentTarget.style.backgroundColor = '#7f8986ff';
        e.currentTarget.style.color = '#fff';
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
    };

    const handleMouseLeave = (e) => {
        e.currentTarget.style.backgroundColor = '#fff';
        e.currentTarget.style.color = '#000';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
    };

    return (
        <>
            {/* Hero Section */}
            <section
                style={{
                    position: "relative",
                    height: "90vh",
                    backgroundImage: `url('./em3.png')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    textAlign: "center"
                }}
            >
                <div style={{ background: "rgba(0,0,0,0.4)", padding: "20px", borderRadius: "10px" }}>
                    <p style={{ fontSize: "1.2rem" }}>Providing clarity and confidence through expert audits</p>
                    <h1 style={{ fontWeight: "bold", fontSize: "2.5rem" }}>Ensure accuracy, mitigate risks, and foster trust in your business</h1>
                    
                    {/* WhatsApp Button */}
                    <a
                        href="https://wa.me/+971588124755"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-btn"
                    >
                        <i className="bi bi-whatsapp me-2"></i> Chat on WhatsApp
                    </a>
                </div>
            </section>

            <main>
                {/* Intro Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#fff' }}>
                    <Container>
                        <p style={leadStyle} data-aos="fade-up">
                            At <strong>Emkan</strong>, our <b>Audit & Assurance Services</b> go beyond mere financial verification. We provide a comprehensive and objective review of your business operations to enhance transparency, improve governance, and build stakeholder trust. An effective audit is a powerful tool for strategic decision-making and sustainable growth.
                            <details>
                                <summary>Read More</summary>
                                <p>
                                    Our audit services are intended to do more than just confirm numbers.  We analyze your accounts, systems, and procedures in a thorough and expert manner to guarantee correctness, consistency, and adherence to UAE laws and international standards.  Our audit solutions are tailored to your size and complexity, regardless of whether you are a major corporation, medium-sized firm, or small enterprise.

Our team of certified auditors at Emkan blends industry knowledge with technological proficiency. We examine your financial records, assess internal controls, and spot any vulnerabilities or hazards that might have an impact on your company. More significantly, we offer doable suggestions to boost productivity, fortify financial control, and lower risk exposure.

We think that audits can offer value. We use the process to provide you with deeper insights into your operations rather than considering it as a checklist exercise. Our method turns the audit into a strategic advantage by assisting you in finding areas for cost savings, optimizing internal processes, and enhancing overall financial performance.

Our auditing services are customized to meet the specific needs of each company. We guarantee that you not only fulfill your regulatory responsibilities but also improve your reputation for honesty and openness through statutory audits, internal audits, and compliance reviews.

By selecting Emkan, you collaborate with experts who provide more than just an account viewpoint. We give you confidence, direction, and assurance so you can concentrate on expansion knowing your financial reporting is in capable hands.
                                </p>
                            </details>
                        </p>
                    </Container>
                </section>

                {/* Services Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#7d94a6ff' }}>
                    <Container>
                        <h2 className="text-white fw-bold text-center mb-5" data-aos="fade-up">Our Audit & Assurance Services</h2>
                        <Row className="g-4">
                            {services.map((service, index) => (
                                <Col md={4} key={index} data-aos="zoom-in" data-aos-delay={index * 100}>
                                    <Card
                                        style={cardBaseStyle}
                                        className="p-4 text-center"
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <Card.Body>
                                            <FontAwesomeIcon
                                                icon={service.icon}
                                                className={`fs-1 mb-3 ${service.color || 'text-primary'}`}
                                            />
                                            <h5 className="fw-bold">{service.title}</h5>
                                            <p>{service.description}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>

                {/* Why Choose Us Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#7d94a6ff' }}>
                    <Container>
                        <h2 className="text-white fw-bold text-center mb-5" data-aos="fade-up">Why Choose Emkan for Audit & Assurance?</h2>
                        <Row className="g-4">
                            {reasons.map((reason, index) => (
                                <Col md={4} key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                                    <Card
                                        style={cardBaseStyle}
                                        className="p-4"
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <Card.Body>
                                            <h5 className="fw-bold">{reason.title}</h5>
                                            <p>{reason.description}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            </main>
        </>
    );
};

export default Auditax;